import { useLogger } from "@amzn/xb-hooks";
import React from "react";
import { ErrorBoundary } from "../error-boundary/ErrorBoundary";
import { useExperienceProviders } from "../experience-providers";
import { findXpsComponentEndpoint, loadRemoteComponent } from "./federation";
import { LiveComponentSourceProps } from "./LiveComponentSource";

export interface RemoteComponentProps {
  experienceProviderName: string;
  componentName: string;
  /**
   * This fallback is for while the XPS code is being pulled down. Fallbacks for internal state
   * changes (such as  for while an api call resolves) remain the responsibility of the component.
   */
  fallback?: React.ReactNode;

  /**
   * A list of fields that do not error when overridden inside of Remote components.
   */
  fieldOverrideAllowList?: string[];

  /**
   * @deprecated use the {@link LiveComponentSourceProps.flavor} instead
   */
  flavor?: string;
  /**
   * @deprecated use the {@link LiveComponentSourceProps.payload} instead.
   */
  payload?: string;
  /**
   * All the props that will be sent to LiveComponentSource.
   */
  props?: LiveComponentSourceProps;
}

export function RemoteComponent(props: RemoteComponentProps) {
  const { experienceProviderName, componentName, fallback, props: liveProps, ...rest } = props;
  const experienceProviders = useExperienceProviders();
  const { log } = useLogger();

  log.debug(`Looking up the endpoint for ${experienceProviderName}(XPS).`);
  const xpsComponentEndpoint = findXpsComponentEndpoint(experienceProviderName, experienceProviders);
  log.info(`Found ${xpsComponentEndpoint}(endpoint) from ${experienceProviders}.`);

  if (!xpsComponentEndpoint) {
    log.error(`No endpoint found for ${experienceProviderName} from ${experienceProviders}.`);
    return fallback;
  }

  const InnerRemoteComponent = React.lazy(loadRemoteComponent(xpsComponentEndpoint, props));

  return (
    <ErrorBoundary>
      <React.Suspense fallback={fallback}>
        <InnerRemoteComponent {...{ ...rest, ...liveProps }} />
      </React.Suspense>
    </ErrorBoundary>
  );
}
